<script setup lang="ts">
import { computed, useTemplateRef } from 'vue';
import BaseButton from '@/js/account/components/base/BaseButton.vue';
import { useAuth } from '@/js/account/entities/session';
import { accountMeAvatarUpdate } from '@/js/account/entities/user';
import { useFormErrors } from '@/js/account/shared/lib';
import { classNames } from '@/js/lib/bem';

const cn = classNames('profile-avatar-section');

const { user, fetchMe } = useAuth();
const { setFromAxiosError, generalError, clearErrors } = useFormErrors();

const fileInput = useTemplateRef<HTMLInputElement>('fileInput');
const uploading = defineModel<boolean>('uploading', { default: false });

const displayName = computed(() => user.value?.name ?? user.value?.email ?? '');

const avatarUrl = computed(() => {
  const image = user.value?.avatar.large ?? user.value?.avatar.small;

  return image?.url2x ?? image?.url ?? null;
});

const initials = computed(() => displayName.value.charAt(0).toUpperCase());

function openFileDialog(): void {
  if (uploading.value) {
    return;
  }

  fileInput.value?.click();
}

async function onFileChange(event: Event): Promise<void> {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];

  if (!file) {
    return;
  }

  clearErrors();
  uploading.value = true;

  try {
    await accountMeAvatarUpdate(file);
    await fetchMe();
  }
  catch (error) {
    setFromAxiosError(error);
  }
  finally {
    uploading.value = false;
    input.value = '';
  }
}
</script>

<template>
  <section :class="cn()" aria-label="Profile photo">
    <div :class="cn('avatar')">
      <img
        v-if="avatarUrl"
        :src="avatarUrl"
        alt=""
        :class="cn('avatar-image')"
      >
      <span v-else :class="cn('avatar-initials')" aria-hidden="true">
        {{ initials }}
      </span>
    </div>

    <div :class="cn('controls')">
      <input
        ref="fileInput"
        type="file"
        :class="cn('input')"
        accept="image/jpeg,image/png"
        :disabled="uploading"
        @change="onFileChange"
      >
      <BaseButton
        theme="secondary"
        :loading="uploading"
        :class="cn('button')"
        @click="openFileDialog"
      >
        Upload new photo
      </BaseButton>
      <p :class="cn('hint')">
        <span>At least 800x800 px recommended.</span>
        <span>JPG or PNG is allowed.</span>
      </p>
      <p v-if="generalError" :class="cn('error')" role="alert">
        {{ generalError }}
      </p>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.profile-avatar-section {
  display: flex;
  align-items: center;
  gap: 32px;
  margin-bottom: 32px;

  &__avatar {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 133px;
    height: 133px;
    border-radius: 50%;
    overflow: hidden;
    background-color: var(--color-primary);
  }

  &__avatar-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &__avatar-initials {
    color: var(--color-white);
    font-family: var(--font-family-sans);
    font-size: 64px;
    font-weight: var(--font-weight-regular);
    line-height: 1;
  }

  &__controls {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    min-width: 0;
  }

  &__input {
    display: none;
  }

  &__button {
    color: var(--color-primary);
    margin: 0 0 8px;
  }

  &__hint {
    margin: 0;
    color: var(--color-accent);
    font-family: var(--font-family-sans);
    font-size: 14px;
    line-height: 1.3;
    max-width: 280px;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__error {
    margin: 0;
    color: var(--base-field-error-color, #db1515);
    font-family: var(--font-family-sans);
    font-size: 12px;
    line-height: 1.3;
  }

  @media (max-width: 768px) {
    gap: 16px;

    &__avatar {
      width: 64px;
      height: 64px;
      align-self: flex-start;
    }

    &__avatar-initials {
      font-size: 48px;
    }
  }
}
</style>
